
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

type BlogPost = {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  author: string;
  published: boolean;
  published_at: string;
  meta_description: string;
  meta_keywords: string;
  category: string;
};

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("category")
        .eq("published", true)
        .order("category");

      if (error) throw error;
      
      // Get unique categories
      const uniqueCategories = Array.from(new Set(data.map(post => post.category)));
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchPosts = async () => {
    try {
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .order("published_at", { ascending: false });

      if (selectedCategory) {
        query = query.eq("category", selectedCategory);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Refetch posts when category changes
  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar />

      <Helmet>
        <title>Blog - Feednatic | User Feedback Management Tips & Insights</title>
        <meta
          name="description"
          content="Discover insights about user feedback management, product development, and customer-driven innovation. Learn how to build better products with user feedback."
        />
        <meta
          name="keywords"
          content="user feedback, product management, customer feedback, product development, feature prioritization, startup tips, product insights"
        />
        <link rel="canonical" href="https://feednatic.com/blog" />
        
        {/* OpenGraph tags */}
        <meta property="og:title" content="Blog - Feednatic | User Feedback Management Tips & Insights" />
        <meta
          property="og:description"
          content="Discover insights about user feedback management, product development, and customer-driven innovation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://feednatic.com/blog" />
        <meta property="og:site_name" content="Feednatic" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog - Feednatic" />
        <meta
          name="twitter:description"
          content="Discover insights about user feedback management, product development, and customer-driven innovation."
        />
        
        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Feednatic Blog",
            "description": "Discover insights about user feedback management, product development, and customer-driven innovation.",
            "url": "https://feednatic.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Feednatic",
              "url": "https://feednatic.com"
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-b from-purple-950/20 to-transparent py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-black text-center mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Product Feedback Insights
          </h1>
          <p className="text-xl text-white/70 text-center max-w-2xl mx-auto">
            Learn about user feedback management, product development, and building
            customer-centric products
          </p>
        </div>
      </header>

      {/* Category Filter */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 justify-center">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            onClick={() => setSelectedCategory(null)}
          >
            All Posts
          </Button>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center">Loading posts...</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Card
                key={post.id}
                className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 hover:border-purple-500/50"
                onClick={() => navigate(`/blog/${post.slug}`)}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="object-cover w-full h-full hover:scale-105 transition-transform"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="text-sm text-white/70 mb-3">
                    {formatDistanceToNow(new Date(post.published_at), {
                      addSuffix: true,
                    })}
                    {" · "}
                    {post.author}
                    {" · "}
                    <span className="text-purple-400">{post.category}</span>
                  </div>
                  <h2 className="text-xl font-bold mb-2 line-clamp-2 text-white">
                    {post.title}
                  </h2>
                  <p className="text-white/70 line-clamp-3">
                    {post.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Blog;
